import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAsyncEffect } from '@recargas-dominicanas/core/utils';
import { BusinessStore } from '../../store/businessStore';
import { CustomerCard } from '../../components/customer-card/customer-card.component';
import { Deposits } from '../../components/deposits/deposits.component';
import { SalesReports } from '../../components/sales-reports/sales-reports.component';
import { TabsPane } from '../../components/tabs-pane/tabs-pane.component';
import { EditCustomerModal } from './edit-customer-modal/edit-customer-modal.component';
import { BusinessTransactions } from './business-transactions/business-transactions.component';
import { CustomerUser } from './customer-user/customer-user.component';
import { Products } from './products/products.component';
import { CustomerProducts } from './customer-products/products.component';
import { CustomerApi } from '@recargas-dominicanas/core/adminApi';
import type { IDeposit } from '@recargas-dominicanas/core/types';
import { CustomerDeposits } from './customer-deposits/customer-deposits.component';
import { BusinessUsers } from './business-users/business-users.component';

import { 
  Case, 
  Icon,
  PageContainer, 
  PageContent, 
  PageToolbar, 
  SalesReport, 
  Switch, 
  Tab, 
  TabBar, 
  ToolbarTitle 
} from '@recargas-dominicanas/core/components';

import { style } from './customer.module.css';

export function Customer() {

  const params = useParams<{ id: string }>();
  const id = parseInt(params.id!);
  const business = BusinessStore.getById(id);
  const deposits = BusinessStore.getDeposits(id);
  const salesReports = BusinessStore.getSalesReports(id);
  const [selectedTab, setSelectedTab] = useState('transactions');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [customerBalance, setCustomerBalance] = useState(0);
  const [customerDeposits, setCustomerDeposits] = useState<IDeposit[]>([]);

  useAsyncEffect(async () => {

    if (!business) {
      await BusinessStore.fetchById(id);
    }

    await BusinessStore.fetchTransactions(id, 1, 100);
    await BusinessStore.fetchDeposits(id);
    await BusinessStore.fetchSalesReports(id);
    await BusinessStore.fetchBusinessUsers(id);
  });

  useEffect(() => {
    fetch();
  }, [business.customerId]);

  function fetch() {

    CustomerApi
      .getBalance(business.customerId)
      .then(balance => setCustomerBalance(balance.amount))
      .catch(console.error);

    CustomerApi
      .getDeposits(business.customerId, 1, 100)
      .then((deposits) => setCustomerDeposits(deposits.data))
      .catch(console.error);
  }

  function handleTabClick(tab: any) {
    setSelectedTab(tab.tag);
  }

  function handleOpenEditCustomer() {
    setOpenEditModal(true);
  }

  function handleCloseEditCustomer() {
    setOpenEditModal(false);
  }

  if (!business) return null;

  return (
    <PageContainer className={style.container}>
      <PageToolbar>
        <ToolbarTitle 
          style={style.toolbarTitle}
          title={business.name}/>
        <div className={style.toolbarRight}>
          <SalesReport
            style={style.salesReport} 
            balance={business.balance}
            sales={business.sales}
            profit={business.profit}/>
          <div className={style.options}>
            <Icon 
              className={style.option} 
              icon='edit'
              onClick={handleOpenEditCustomer}/>
          </div>
        </div>
      </PageToolbar>
      <PageContent className={style.content}>
        <CustomerCard
          style={style.customerCard}
          customer={business.customer}
          customerBalance={customerBalance}
          business={business}/>
        <TabsPane>
          <TabBar onTabClick={handleTabClick}>
            <Tab tag='transactions' title='Transacciones'/>
            <Tab tag='products' title='Productos'/>
            <Tab tag='deposits' title='Depositos'/>
            <Tab tag='sales-reports' title='Cierre de ventas'/>
            <Tab tag='users' title='Usuarios'/>
            <Tab tag='customer' title='Cliente'/>
            <Tab tag='customer-products' title='Cliente productos'/>
            <Tab tag='customer-deposits' title='Cliente Depositos'/>
          </TabBar>
          <Switch caseId={selectedTab}>
            <Case caseId='transactions'><BusinessTransactions id={business.id}/></Case>
            <Case caseId='products'><Products businessId={id}/></Case>
            <Case caseId='deposits'><Deposits deposits={deposits}/></Case>
            <Case caseId='sales-reports'><SalesReports salesReports={salesReports}/></Case>
            <Case caseId='users'><BusinessUsers businessId={business.id}/></Case>
            <Case caseId='customer'><CustomerUser businessId={business.id} customer={business.customer}/></Case>
            <Case caseId='customer-products'><CustomerProducts business={business}/></Case>
            <Case caseId='customer-deposits'>
              <CustomerDeposits 
                customerId={business.customerId} 
                deposits={customerDeposits}
                onUpdate={fetch}/>
            </Case>
          </Switch>
        </TabsPane>
      </PageContent>
      <EditCustomerModal 
        open={openEditModal}
        business={business}
        onClose={handleCloseEditCustomer}/>
    </PageContainer>
  );
}
